// src/redux/slices/returnsSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";

import api from "api/api";
import endpoint from "api/endpoints";

/**
 * Fetch a customer's Return Authorizations. No fromDate/toDate/status/sort
 * filter machinery like `purchaseHistorySlice` — return volume per customer
 * is far smaller than order volume, so `useReturnProcess` filters the loaded
 * list client-side instead of asking this slice to.
 */
export const fetchReturns = createAsyncThunk(
  "returns/fetchReturns",
  async ({ userId, limit, getAccessTokenSilently }, { rejectWithValue }) => {
    try {
      const token = await getAccessTokenSilently();
      const url = endpoint.GET_RETURN_REQUESTS({ userId, limit });
      const res = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data || {};
      return { returns: data.items || [] };
    } catch (err) {
      const status = err?.response?.status;
      const errorMessage =
        err?.response?.data?.error || "Failed to fetch returns";

      if (status === 403) {
        return rejectWithValue({
          type: "FORBIDDEN",
          message: "Access forbidden: You can only view your own returns",
          status: 403,
        });
      }
      if (status === 401) {
        return rejectWithValue({
          type: "UNAUTHORIZED",
          message: "Authentication required",
          status: 401,
        });
      }
      return rejectWithValue({
        type: "GENERAL_ERROR",
        message: errorMessage,
        status: status || 500,
      });
    }
  }
);

/**
 * The Sales Orders the "start a return" dialog can return against.
 *
 * Deliberately its own fetch rather than reusing `purchaseHistorySlice`:
 * that slice holds one untyped `transactions` array shared with the Orders
 * page and `RecentPurchases`, so whichever page loaded last decided both the
 * limit and the type mix everyone else saw. Filtering to `SalesOrd`
 * server-side also makes `limit` mean "this many orders" — filtering after
 * the fetch returned roughly a fifth as many orders as asked for, because
 * each order also produces an invoice, a fulfillment and usually a payment.
 */
export const fetchReturnableOrders = createAsyncThunk(
  "returns/fetchReturnableOrders",
  async ({ userId, limit, getAccessTokenSilently }, { rejectWithValue }) => {
    try {
      const token = await getAccessTokenSilently();
      const url = endpoint.GET_TRANSACTION_BY_ID({
        userId,
        limit,
        type: "SalesOrd",
      });
      const res = await api.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = res.data || {};
      return { orders: Array.isArray(data) ? data : data.items || [] };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.error || "Failed to fetch orders"
      );
    }
  }
);

/**
 * The Return Reason list (`customlist_dnd_returnreason`). NetSuite requires
 * `custbody_returnreason` on every RMA, so the dialog's reason dropdown is
 * driven from this rather than from hardcoded options.
 */
export const fetchReturnReasons = createAsyncThunk(
  "returns/fetchReturnReasons",
  async ({ getAccessTokenSilently }, { rejectWithValue }) => {
    try {
      const token = await getAccessTokenSilently();
      const res = await api.get(endpoint.GET_RETURN_REASONS(), {
        headers: { Authorization: `Bearer ${token}` },
      });
      return { reasons: res.data?.items || [] };
    } catch (err) {
      return rejectWithValue(
        err?.response?.data?.error || "Failed to fetch return reasons"
      );
    }
  }
);

const initialState = {
  returns: [],
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed' | 'forbidden' | 'unauthorized'
  error: null,
  lastErrorType: null,
  authenticationFailed: false,
  lastFetch: null,
  reasons: [],
  reasonsStatus: "idle",
  returnableOrders: [],
  returnableOrdersStatus: "idle",
};

const returnsSlice = createSlice({
  name: "returns",
  initialState,
  reducers: {
    clearReturns() {
      return initialState;
    },

    /**
     * Mark the list stale *before* a refetch is actually dispatched.
     *
     * Creating a return is followed by a deliberately delayed refetch (see
     * `useReturnProcess`), because SuiteQL doesn't always surface a
     * just-created record immediately. Without this the list would keep
     * rendering its pre-submit contents during that gap — the customer
     * submits a return and watches a list that doesn't contain it. Flipping
     * to `loading` up front means they see the list's normal loading state
     * instead, and the new return simply arrives.
     */
    markReturnsRefreshing(state) {
      state.status = "loading";
    },

    /**
     * Refetch the list next time it's shown. For a change made somewhere
     * other than the list page — booking a pickup on the detail page — which
     * would otherwise leave "Approved — book pickup" on the row until the
     * 5-minute staleness ran out.
     */
    invalidateReturns(state) {
      state.lastFetch = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReturns.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchReturns.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.returns = action.payload.returns || [];
        state.lastFetch = Date.now();
      })
      .addCase(fetchReturns.rejected, (state, action) => {
        const errorPayload = action.payload;
        if (errorPayload?.type === "FORBIDDEN") {
          state.status = "forbidden";
          state.authenticationFailed = true;
          state.lastErrorType = "FORBIDDEN";
          state.error = errorPayload.message;
        } else if (errorPayload?.type === "UNAUTHORIZED") {
          state.status = "unauthorized";
          state.authenticationFailed = true;
          state.lastErrorType = "UNAUTHORIZED";
          state.error = errorPayload.message;
        } else {
          state.status = "failed";
          state.lastErrorType = "GENERAL_ERROR";
          state.error = errorPayload?.message || "Failed to fetch returns";
        }
      })

      .addCase(fetchReturnableOrders.pending, (state) => {
        state.returnableOrdersStatus = "loading";
      })
      .addCase(fetchReturnableOrders.fulfilled, (state, action) => {
        state.returnableOrdersStatus = "succeeded";
        state.returnableOrders = action.payload.orders || [];
      })
      .addCase(fetchReturnableOrders.rejected, (state) => {
        state.returnableOrdersStatus = "failed";
      })

      .addCase(fetchReturnReasons.pending, (state) => {
        state.reasonsStatus = "loading";
      })
      .addCase(fetchReturnReasons.fulfilled, (state, action) => {
        state.reasonsStatus = "succeeded";
        state.reasons = action.payload.reasons || [];
      })
      .addCase(fetchReturnReasons.rejected, (state) => {
        state.reasonsStatus = "failed";
      });
  },
});

export const { clearReturns, markReturnsRefreshing, invalidateReturns } =
  returnsSlice.actions;

export const selectReturnRequests = (state) => state.returns.returns;
export const selectReturnsStatus = (state) => state.returns.status;
export const selectReturnsError = (state) => state.returns.error;
export const selectReturnReasons = (state) => state.returns.reasons;
export const selectReturnReasonsStatus = (state) => state.returns.reasonsStatus;
export const selectReturnableOrders = (state) => state.returns.returnableOrders;
export const selectReturnableOrdersStatus = (state) =>
  state.returns.returnableOrdersStatus;

export const selectShouldFetchReturns = (state) => {
  const { status, lastFetch, authenticationFailed } = state.returns;
  if (
    authenticationFailed ||
    status === "forbidden" ||
    status === "unauthorized"
  ) {
    return false;
  }
  // A failed fetch stays failed. `lastFetch` is only set on success, so
  // without this a 500 or network error flipped this selector back to true
  // and `useReturnProcess`'s effect re-dispatched `fetchReturns` forever
  // (loading → failed → loading …). Recovery is a user action: a reload, or
  // `invalidateReturns` after the status has been reset.
  if (status === "failed") return false;
  const isStale = lastFetch && Date.now() - lastFetch > 5 * 60 * 1000;
  return (!lastFetch && status !== "loading") || isStale;
};

export default returnsSlice.reducer;
